import { Box } from '@mui/material';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type SpendProductSupplierRowDto } from 'entities/ReportSpendSummary';
import {
    ComposableReportWidget,
    type ReportWidgetItem,
    augmentItemsForTopNOther,
    buildTopNChartItemsWithOther,
} from 'widgets/ComposableReportWidget';

import { getProductSuppliersWidgetConfig } from '../model/lib/getProductSuppliersWidgetConfig';
import { mapProductSuppliersToWidgetItems } from '../model/lib/mapProductSuppliersToWidgetItems';

const PIE_MAX_ITEMS = 5;

interface SpendProductSuppliersSectionProps {
    suppliers: SpendProductSupplierRowDto[];
    totalSpend: number;
    loading?: boolean;
}

export const SpendProductSuppliersSection = memo(
    ({
        suppliers,
        totalSpend,
        loading = false,
    }: SpendProductSuppliersSectionProps) => {
        const { t } = useTranslation();

        const items: ReportWidgetItem[] = useMemo(() => {
            const sorted = mapProductSuppliersToWidgetItems(suppliers);

            return augmentItemsForTopNOther({
                items: sorted,
                topCount: PIE_MAX_ITEMS,
                otherChartItemId: 'spend-summary-product-suppliers-other',
            });
        }, [suppliers]);

        const chartItems = useMemo(() => {
            if (items.length <= PIE_MAX_ITEMS) {
                return undefined;
            }

            return buildTopNChartItemsWithOther({
                items,
                totalAmount: String(totalSpend),
                topCount: PIE_MAX_ITEMS,
                otherItemId: 'spend-summary-product-suppliers-other',
                otherLabel: t('Other'),
            });
        }, [items, t, totalSpend]);

        const config = useMemo(
            () => getProductSuppliersWidgetConfig({ t }),
            [t],
        );

        return (
            <Box
                data-testclass="spend-summary-product-suppliers-section"
                sx={{
                    borderBottom: '1px solid #E0E0E0',
                    paddingBottom: '40px',
                }}
            >
                <ComposableReportWidget
                    config={config}
                    items={items}
                    chartItems={chartItems}
                    loading={loading}
                />
            </Box>
        );
    },
);

SpendProductSuppliersSection.displayName = 'SpendProductSuppliersSection';
